import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Grid,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  Typography
} from "@material-ui/core/";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Aux from "../../../hoc/Auxilliary";
import Backdrop from "../../Backdrop";
import MessageIndicator from "../../SnackBar";
import {
  getFacultyGradesData,
  saveFacultyGradesData,
  getFacultyClassData,
} from "../../../reduxSlices/classesSlice";
import localizedStrings from "../../../utils/localize";

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: "#005598",
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))(TableRow);

const useStyles = makeStyles(() => ({
  buttonRow: {
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: "1rem",
  },
  section: {
    marginBottom: "1.5rem",
  },
  sectiontop: {
    marginBottom: "1.5rem",
    width:"fit-content",
  },
  formControl: {
    minWidth: 140,
  },
  smallTableCell: {
    minWidth: "48px",
    width: "1px",
    whiteSpace: "nowrap",
    padding: "6px 12px",
  },
  narrowTable: {
    '& td, & th': {
      padding: '6px 12px',
      whiteSpace: 'nowrap',
    },
  },
  classConatiner: {
    padding: "1rem 0",
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  classFont: {
    fontSize: "1.8rem",
    fontWeight: 500,
    flex: 1,
  },
}));

const gradeScale = [
  { letter: "A+", min: 90, max: 100 },
  { letter: "A", min: 80, max: 89.99 },
  { letter: "B+", min: 75, max: 79.99 },
  { letter: "B", min: 70, max: 74.99 },
  { letter: "C", min: 60, max: 69.99 },
  { letter: "D", min: 50, max: 59.99 },
  { letter: "F", min: 0, max: 49.99 },
];

const commentOptions = [
  { value: "", label: "" },
  { value: "I", label: "I" },
  { value: "NP", label: "NP" },
];

const isNullCommentValue = (value) =>
  value === null || value === undefined || value === "" || value === "null" || value === "NULL";

// Map backend numeric codes to dropdown values and back
const commentCodeToValue = {
  "943240004": "I",
  "943240010": "NP",
};

const commentValueToCode = {
  I: 943240004,
  NP: 943240010,
};

const normalizeGradeInput = (value) =>
  typeof value === "string" ? value.replace(/\./g, ",") : "";

const normalizeGradeForSave = (value) =>
  typeof value === "string" ? value.replace(/,/g, ".") : value;

const parseGradeValue = (value) => {
  if (value === null || value === undefined || value === "") return NaN;
  const normalized = String(value).replace(/,/g, ".");
  return Number(normalized);
};

const isGradeValid = (value) => {
  if (value === null || value === undefined || value === "") return false;
  const normalized = normalizeGradeInput(String(value));
  if (!/^[0-9]{1,3}(?:,[0-9]{0,2})?$/.test(normalized)) return false;
  const numeric = parseGradeValue(normalized);
  return !Number.isNaN(numeric) && numeric >= 0 && numeric <= 100;
};

const getLetterGrade = (gradeValue) => {
  if (!isGradeValid(gradeValue)) return "";
  const grade = parseGradeValue(gradeValue);
  if (grade >= 90) return "A+";
  if (grade >= 80) return "A";
  if (grade >= 75) return "B+";
  if (grade >= 70) return "B";
  if (grade >= 60) return "C";
  if (grade >= 50) return "D";
  return "F";
};

function ClassGrades(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [gradeRows, setGradeRows] = useState([]);
  const [readOnlyMode, setReadOnlyMode] = useState(false);
  const [message, setMessage] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const classData = useSelector((state) => state.classData.classData?.data);
  const facultyGradeData = useSelector((state) => state.classData.facultyGradeData?.data);
  const dataFetchingStatus = useSelector(
    (state) => state.classData.dataFetchingStatus,
  );
  //current lagnguage
  const language = useSelector((state) => state.language.language);
  //set the current language to localize string
  localizedStrings.setLanguage(language);
  useEffect(() => {
    localizedStrings.setLanguage(language);
  }, [language]);

    useEffect(() => {
      dispatch(
        getFacultyClassData(window.localStorage.getItem("x-auth-contactId")),
      );
    }, [dispatch]);

  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const classId = searchParams.get("classId");
  const classInfo = classData
    ? classData.find((item) => String(item.ses_classid) === String(classId))
    : null;

  const courseDisplayName = [
    classInfo?.ses_courseid,
    classInfo?.ses_institutionclassid,
    classInfo?.ses_coursename || classInfo?.ses_classname,
  ]
    .filter(Boolean)
    .join(" - ")
    .trim();

  const termDisplayName = classInfo?.ses_termname || "";

  useEffect(() => {
    if (!classId) {
      history.replace("/faculty/classes");
      return;
    }
    dispatch(getFacultyGradesData(classId));
  }, [classId, dispatch, history]);

  useEffect(() => {
    if (facultyGradeData && Array.isArray(facultyGradeData.students)) {
      const students = facultyGradeData.students;
      setGradeRows(
        students.map((row) => ({
          ...row,
          ses_sessiongrade1:
            row.ses_sessiongrade1 !== undefined && row.ses_sessiongrade1 !== null
              ? normalizeGradeInput(String(row.ses_sessiongrade1))
              : "",
          ses_graderreceived: isGradeValid(row.ses_sessiongrade1)
            ? row.ses_gradereceived || getLetterGrade(row.ses_sessiongrade1)
            : "",
          usb_gradecomment: isNullCommentValue(row.usb_gradecomment)
            ? ""
            : commentCodeToValue[String(row.usb_gradecomment)] || "",
        })),
      );
      setReadOnlyMode(!!facultyGradeData.usb_dategradessubmitted);
    } else {
      setGradeRows([]);
    }
  }, [facultyGradeData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = event?.target?.value;
    if (value == null) {
      return;
    }
    setRowsPerPage(+value);
    setPage(0);
  };

  const handleBack = () => {
    history.push("/faculty/classes");
    setTimeout(() => {
              window.location.reload();
            }, 100);
  };

  const handleGradeChange = (event, index) => {
    const value = normalizeGradeInput(event?.target?.value || "");
    if (value !== "" && !/^[0-9]{0,3}(?:,[0-9]{0,2})?$/.test(value)) {
      return;
    }
    setGradeRows((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        ses_sessiongrade1: value,
        ses_graderreceived: getLetterGrade(value),
      };
      return next;
    });
  };

  const handleCommentChange = (event, index) => {
    const value = event?.target?.value || "";
    setGradeRows((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        usb_gradecomment: value,
      };
      return next;
    });
  };

  const prepareGradesForSave = () =>
    gradeRows.map((row) => {
      const gradeRaw =
        row.ses_sessiongrade1 === "" || row.ses_sessiongrade1 === null
          ? null
          : normalizeGradeForSave(row.ses_sessiongrade1);
      const gradeNumber = gradeRaw === null ? null : Number(gradeRaw);
      const commentValue = row.usb_gradecomment;

      return {
        ...row,
        ses_sessiongrade1: Number.isNaN(gradeNumber) ? null : gradeNumber,
        usb_gradecomment:
          isNullCommentValue(commentValue)
            ? null
            : commentValueToCode[commentValue] || null,
      };
    });

  const handleSave = () => {
    if (hasInvalidGrades) {
      setMessage("Please correct invalid grades before saving.");
      return;
    }
    dispatch(
      saveFacultyGradesData({
        classId,
        action: "save",
        grades: prepareGradesForSave(),
      }),
    );
    setSnackbarMessage(localizedStrings.facultyAndStuff.classgrades.saved);
    setShowSnackbar(true);
  };

  const handleSnackbarClose = () => {
    setShowSnackbar(false);
  };

  const handleSubmit = () => {
    if (hasInvalidGrades) {
      setMessage("Please correct invalid grades before submitting.");
      return;
    }
    dispatch(
      saveFacultyGradesData({
        classId,
        action: "submit",
        grades: prepareGradesForSave(),
      }),
    );
    setReadOnlyMode(true);
    setSnackbarMessage(localizedStrings.facultyAndStuff.classgrades.finalGradesSaved);
    setShowSnackbar(true);
    setMessage("Final grades submitted. All fields are now read-only.");
  };

  const isStudentAuditing = (row) => {
    return row.ses_audited === true || row.ses_audited === "true";
  };

  const allGradesEntered =
    gradeRows.length > 0 &&
    gradeRows.every((row) => {
      if (isStudentAuditing(row)) return true;
      const gradeValue = row.ses_sessiongrade1;
      if (gradeValue === null || gradeValue === "" || gradeValue === undefined) {
        return false;
      }
      return isGradeValid(gradeValue);
    });

  const hasInvalidGrades = gradeRows.some((row) => {
    if (isStudentAuditing(row)) return false;
    return row.ses_sessiongrade1 !== "" && !isGradeValid(row.ses_sessiongrade1);
  });

  const classIsFinalized =
    Boolean(facultyGradeData?.usb_dategradessubmitted) || readOnlyMode;

  const formatYMD = (val) => {
    if (!val) return "";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return String(val);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}/${mm}/${dd}`;
  };

  const tableData = gradeRows
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    .map((row, index) => {
      const readOnly = classIsFinalized || isStudentAuditing(row);
      return (
        <StyledTableRow key={row.ses_sessionid || index}>
          <StyledTableCell>{row.lastname}</StyledTableCell>
          <StyledTableCell>{row.ses_preferredname}</StyledTableCell>
          <StyledTableCell style={{width:"8%"}}>
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              type="text"
              inputProps={{
                inputMode: "decimal",
                pattern: "[0-9]*([.,][0-9]{0,2})?",
                readOnly: readOnly,
              }}
              value={row.ses_sessiongrade1}
              onChange={(event) => handleGradeChange(event, page * rowsPerPage + index)}
              disabled={readOnly}
              error={!readOnly && row.ses_sessiongrade1 !== "" && !isGradeValid(row.ses_sessiongrade1)}
              helperText={!readOnly && row.ses_sessiongrade1 !== "" && !isGradeValid(row.ses_sessiongrade1) ? "Enter a grade between 0,00 and 100,00" : ""}
            />
          </StyledTableCell>
          <StyledTableCell>
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              value={row.ses_graderreceived || ""}
              InputProps={{ readOnly: true }}
              disabled
            />
          </StyledTableCell>
          <StyledTableCell>
            <Select
              value={row.usb_gradecomment || ""}
              onChange={(event) => handleCommentChange(event, page * rowsPerPage + index)}
              disabled={readOnly}
              className={classes.formControl}
            >
              {commentOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label || '\u00A0'}
                </MenuItem>
              ))}
            </Select>
          </StyledTableCell>
        </StyledTableRow>
      );
    });

  return (
    <Aux>
      <MessageIndicator
        open={showSnackbar}
        message={snackbarMessage}
        handleClose={handleSnackbarClose}
      />
      <Backdrop show={dataFetchingStatus} />
      <Grid container direction="column">

        <Grid className={classes.classConatiner}>
          <Typography component="div" className={classes.classFont}>
             {localizedStrings.facultyAndStuff.classgrades.grades}
          </Typography>
          <Typography component="div">
            <Button variant="outlined" onClick={handleBack}>
              {localizedStrings.facultyAndStuff.classgrades.back}
            </Button>
          </Typography>
        </Grid>

        {courseDisplayName && (
          <Grid item style={{ marginBottom: "0.25rem" }}>
            <Typography variant="h6" component="div">
              {courseDisplayName}
            </Typography>
          </Grid>
        )}

        {termDisplayName && (
          <Grid item style={{ marginBottom: "1rem" }}>
            <Typography variant="subtitle1" component="div">
              {termDisplayName}
            </Typography>
          </Grid>
        )}
        {facultyGradeData?.usb_dategradessubmitted && (
          <Grid item style={{ marginBottom: "1rem" }}>
            <Typography variant="body2" component="div">
              {localizedStrings.facultyAndStuff.classgrades.dateGradesSubmitted}: {formatYMD(facultyGradeData.usb_dategradessubmitted)}
            </Typography>
          </Grid>
        )}
        {facultyGradeData?.ses_dategradesfinalized && (
          <Grid item style={{ marginBottom: "1rem" }}>
            <Typography variant="body2" component="div">
              {localizedStrings.facultyAndStuff.classgrades.dateGradesPosted}: {formatYMD(facultyGradeData.ses_dategradesfinalized)}
            </Typography>
          </Grid>
        )}
        <Grid item style={{ marginTop: "1rem", marginBottom: "1rem" }}>
          <Typography variant="body2" component="div">
            {localizedStrings.facultyAndStuff.classgrades.gradeChangeNotice}
          </Typography>
        </Grid>
        {message && (
          <Grid item style={{ marginBottom: "1rem", color: "#005598" }}>
            {message}
          </Grid>
        )}
 
        <Grid item className={classes.sectiontop}>
          <Paper elevation={1} style={{ padding: "1rem" }}>
            <h3>{localizedStrings.facultyAndStuff.classgrades.gradescale}</h3>
            <TableContainer component={Paper}>
              <Table size="small" className={classes.narrowTable}>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>{localizedStrings.facultyAndStuff.classgrades.letterGrade}</StyledTableCell>
                    <StyledTableCell>{localizedStrings.facultyAndStuff.classgrades.beginningNumber}</StyledTableCell>
                    <StyledTableCell>{localizedStrings.facultyAndStuff.classgrades.endingNumber}</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {gradeScale.map((scale) => (
                    <StyledTableRow key={scale.letter}>
                      <StyledTableCell>{scale.letter}</StyledTableCell>
                      <StyledTableCell>{Number(scale.min).toFixed(2).replace('.', ',')}</StyledTableCell>
                      <StyledTableCell>{Number(scale.max).toFixed(2).replace('.', ',')}</StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
 <br />

        <Grid item>
          <TableContainer component={Paper} className={classes.section}>
            <Table size="small" className={classes.narrowTable} aria-label="Grades table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>{localizedStrings.facultyAndStuff.classgrades.lastname}</StyledTableCell>
                  <StyledTableCell>{localizedStrings.facultyAndStuff.classgrades.preferredname}</StyledTableCell>
                  <StyledTableCell className={classes.smallTableCell}>{localizedStrings.facultyAndStuff.classgrades.grade}</StyledTableCell>
                  <StyledTableCell className={classes.smallTableCell}>{localizedStrings.facultyAndStuff.classgrades.letterGrade}</StyledTableCell>
                  <StyledTableCell className={classes.smallTableCell}>{localizedStrings.facultyAndStuff.classgrades.gradeComment}</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>{tableData}</TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={gradeRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </TableContainer>
        </Grid>



        <Grid item className={classes.buttonRow}>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleSave}
              disabled={
                classIsFinalized ||
                gradeRows.length === 0 ||
                hasInvalidGrades
              }
              style={{ marginRight: 8 }}
            >
             {localizedStrings.facultyAndStuff.classgrades.save}
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={!allGradesEntered || classIsFinalized}
            >
              {localizedStrings.facultyAndStuff.classgrades.submitFinalGrades}

            </Button></div>
        </Grid>



      
      </Grid>
    </Aux>
  );
}

export default ClassGrades;
